/**
 * TalkToast UI Design System Constants
 * Single source of truth for consistent UI elements across the application
 */

// ============================================================================
// BRAND ICON/LOGO
// ============================================================================

/**
 * TalkToast branded icon and logo constants
 * Use LogoIcon / LogoFull from @talktoast/shared for the actual SVG logo.
 * These string constants are kept only for text-only contexts (page titles, etc).
 */
export const BRAND_ICON = {
  logoText: 'TalkToast',
  logoWithText: 'TalkToast',
} as const

// ============================================================================
// MEAL TYPE ICONOGRAPHY
// ============================================================================

export const MEAL_TYPE_ICONS = {
  breakfast: {
    emoji: '🍳',
    label: 'Breakfast',
    shortLabel: 'BF',
    color: 'yellow',
    key: 'breakfast'
  },
  lunch: {
    emoji: '🥗',
    label: 'Lunch',
    shortLabel: 'LN',
    color: 'orange',
    key: 'lunch'
  },
  dinner: {
    emoji: '🍽️',
    label: 'Dinner',
    shortLabel: 'DN',
    color: 'blue',
    key: 'dinner'
  },
  snacks: {
    emoji: '🍿',
    label: 'Snacks',
    shortLabel: 'SN',
    color: 'green',
    key: 'snacks'
  },
  dessert: {
    emoji: '🍰',
    label: 'Dessert',
    shortLabel: 'DS',
    color: 'pink',
    key: 'dessert'
  }
} as const

export type MealTypeKey = keyof typeof MEAL_TYPE_ICONS

// Helper function to get meal emoji
export const getMealEmoji = (type: string): string => {
  const mealType = type as MealTypeKey
  return MEAL_TYPE_ICONS[mealType]?.emoji || '🍽️'
}

// Helper function to get meal label
export const getMealLabel = (type: string): string => {
  const mealType = type as MealTypeKey
  return MEAL_TYPE_ICONS[mealType]?.label || 'Meal'
}

// Helper function to get meal color
export const getMealColor = (type: string): string => {
  const mealType = type as MealTypeKey
  return MEAL_TYPE_ICONS[mealType]?.color || 'gray'
}

// Convert to array format for iteration
export const MEAL_TYPES_ARRAY = Object.values(MEAL_TYPE_ICONS)

// ============================================================================
// LOADING INDICATORS
// ============================================================================

// Loading Spinner Styles
export const LOADING_SPINNER = {
  // Default size for main loading screens
  default: 'animate-spin rounded-full h-16 w-16 border-b-4 border-toast-gold',

  // Small size for inline loading states
  small: 'animate-spin rounded-full h-8 w-8 border-b-2 border-toast-gold',

  // Large size for full-page loading
  large: 'animate-spin rounded-full h-24 w-24 border-b-4 border-toast-gold',

  // Tiny size for button loading states
  tiny: 'animate-spin rounded-full h-4 w-4 border-b-2 border-toast-gold'
} as const

// Loading Screen Background
export const LOADING_BACKGROUND = 'bg-gradient-to-br from-oat-beige to-cream-white'

// Loading Screen Container
export const LOADING_CONTAINER = 'flex items-center justify-center'

// Animated Emojis for Loading Screens
export const LOADING_EMOJIS = {
  mealPlanning: {
    emojis: ['🍳', '🥗', '🍽️'],
    className: 'flex items-center justify-center space-x-2 text-sm text-charcoal'
  },
  shopping: {
    emojis: ['🛒', '📝', '✨'],
    className: 'flex items-center justify-center space-x-2 text-sm text-charcoal'
  }
} as const

// ============================================================================
// NAVIGATION BUTTONS
// ============================================================================

// Back Button
export const BACK_BUTTON = {
  icon: '←',
  className: 'flex items-center gap-2 text-bread-crust hover:text-charcoal transition-colors',
  textClassName: 'text-sm font-medium'
} as const

// Continue/Next Button
export const CONTINUE_BUTTON = {
  icon: '→',
  primary: 'w-full py-2 text-sm bg-avocado-green text-white rounded font-medium hover:bg-avocado-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center',
  secondary: 'w-full py-2 text-sm bg-toast-gold text-white rounded font-medium hover:bg-toast-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center'
} as const

// ============================================================================
// BRAND COLORS v0.1 (From Brand Guide v0.md - Color Palette v0.1)
// ============================================================================

/**
 * Brand Colors v0.1 - Updated palette with semantic roles for dark mode support
 * @see docs/Brand Guide v0.md - Section 3: Color Palette v0.1
 */
export const BRAND_COLORS = {
  // Light mode colors (current)
  warmCream: '#FFF6ED',        // Background (Base)
  butterCream: '#FDEDD9',      // Surface (Cards)
  deepCharcoal: '#33261E',     // Text (Primary)
  toastGold: '#F4A340',        // Accent - unchanged
  avocadoGreen: '#64B05A',     // Accent - adjusted
  coralPop: '#FF7C6A',         // Accent - adjusted
  softTan: '#E8D2B8',          // Outline/Divider
  honeyYellow: '#FFD77E',      // Accent - unchanged

  // Dark mode colors (for future implementation)
  warmCharcoal: '#3A2F28',     // Dark background
  cocoaBrown: '#4B3C33',       // Dark surface
  creamWhiteDark: '#FFF3E4',   // Dark text
  cocoaLine: '#5C4A3E',        // Dark outline

  // Legacy colors (deprecated, keeping for transition)
  breadCrustBrown: '#6B3E1E',  // @deprecated Use deepCharcoal
  creamWhite: '#FFF8DC',       // @deprecated Use warmCream
  charcoal: '#2C2C2C',         // @deprecated Use deepCharcoal
  oatBeige: '#F7E5C0'          // @deprecated Use butterCream
} as const

// ============================================================================
// SEMANTIC COLOR SYSTEM (Brand Palette v0.1 Applied)
// ============================================================================

/**
 * Semantic color system aligned with Brand Palette v0.1
 * Maps functional color roles to brand colors
 * Use these instead of hard-coded Tailwind colors (bg-white, bg-blue-X, bg-gray-X)
 *
 * Migration notes:
 * - bg-white → SEMANTIC_COLORS.surface.* (context dependent)
 * - bg-blue-600 → SEMANTIC_COLORS.interactive.primary
 * - bg-gray-50/100 → SEMANTIC_COLORS.neutral.*
 * - text-gray-600 → SEMANTIC_COLORS.text.*
 */
export const SEMANTIC_COLORS = {
  // Surface colors (backgrounds, cards, panels)
  surface: {
    base: 'bg-warm-cream',           // Page background
    elevated: 'bg-butter-cream',     // Cards, modals, panels
    subtle: 'bg-butter-cream/50',    // Hover states, subtle backgrounds
    white: 'bg-white',               // Pure white (use sparingly - OAuth, special cases)
    overlay: 'bg-black bg-opacity-50' // Modal overlays
  },

  // Text colors
  text: {
    primary: 'text-deep-charcoal',   // Main text
    secondary: 'text-deep-charcoal/70', // Secondary text
    muted: 'text-deep-charcoal/50',  // Muted/disabled text
    inverse: 'text-white',           // Text on dark backgrounds
    brand: 'text-toast-gold'         // Brand-colored text
  },

  // Border colors
  border: {
    default: 'border-soft-tan',      // Standard borders
    subtle: 'border-soft-tan/50',    // Subtle borders
    emphasis: 'border-toast-gold',   // Emphasized borders
    muted: 'border-deep-charcoal/10' // Very subtle borders
  },

  // Interactive elements (buttons, links, selections)
  interactive: {
    primary: 'bg-toast-gold text-white hover:bg-toast-gold/90',        // Primary actions
    primaryText: 'text-toast-gold hover:text-toast-gold/80',           // Primary links
    secondary: 'bg-avocado-green text-white hover:bg-avocado-green/90', // Secondary actions
    secondaryText: 'text-avocado-green hover:text-avocado-green/80',   // Secondary links
    tertiary: 'bg-butter-cream text-deep-charcoal hover:bg-butter-cream/70', // Tertiary actions
    selected: 'bg-toast-gold/20 border-toast-gold',                    // Selected state
    hover: 'hover:bg-butter-cream/70',                                  // Generic hover
    active: 'bg-butter-cream',                                          // Active state
    disabled: 'bg-deep-charcoal/10 text-deep-charcoal/30 cursor-not-allowed' // Disabled
  },

  // Neutral colors (grays - mapped to brand neutrals)
  neutral: {
    50: 'bg-warm-cream',             // Lightest neutral
    100: 'bg-butter-cream',          // Very light neutral
    200: 'bg-soft-tan/30',           // Light neutral
    300: 'bg-soft-tan/50',           // Medium-light neutral
    400: 'bg-soft-tan',              // Medium neutral
    500: 'bg-deep-charcoal/20',      // Medium-dark neutral
    600: 'bg-deep-charcoal/40',      // Dark neutral
    700: 'bg-deep-charcoal/60',      // Darker neutral
    800: 'bg-deep-charcoal/80',      // Very dark neutral
    900: 'bg-deep-charcoal'          // Darkest neutral
  },

  // Feedback colors (success, error, warning, info)
  feedback: {
    success: {
      bg: 'bg-avocado-green/10',
      border: 'border-avocado-green/30',
      text: 'text-avocado-green',
      button: 'bg-avocado-green text-white hover:bg-avocado-green/90'
    },
    error: {
      bg: 'bg-coral-pop/10',
      border: 'border-coral-pop/30',
      text: 'text-coral-pop',
      button: 'bg-coral-pop text-white hover:bg-coral-pop/90'
    },
    warning: {
      bg: 'bg-honey-yellow/20',
      border: 'border-honey-yellow/40',
      text: 'text-honey-yellow',
      button: 'bg-honey-yellow text-deep-charcoal hover:bg-honey-yellow/90'
    },
    info: {
      bg: 'bg-toast-gold/10',
      border: 'border-toast-gold/30',
      text: 'text-toast-gold',
      button: 'bg-toast-gold text-white hover:bg-toast-gold/90'
    }
  }
} as const

// Helper function: Get semantic surface color
export const getSurfaceColor = (type: 'base' | 'elevated' | 'subtle' | 'white' | 'overlay') => {
  return SEMANTIC_COLORS.surface[type]
}

// Helper function: Get semantic text color
export const getTextColor = (type: 'primary' | 'secondary' | 'muted' | 'inverse' | 'brand') => {
  return SEMANTIC_COLORS.text[type]
}

// Helper function: Get semantic border color
export const getBorderColor = (type: 'default' | 'subtle' | 'emphasis' | 'muted') => {
  return SEMANTIC_COLORS.border[type]
}

// Helper function: Get feedback color set
export const getFeedbackColors = (type: 'success' | 'error' | 'warning' | 'info') => {
  return SEMANTIC_COLORS.feedback[type]
}

// ============================================================================
// COMMON UI CLASSES (Brand-aligned) - DEPRECATED: Use SEMANTIC_COLORS instead
// ============================================================================

// Container Backgrounds
/** @deprecated Use SEMANTIC_COLORS.surface instead */
export const CONTAINER_BG = {
  primary: 'bg-cream-white',
  card: 'bg-oat-beige',      // Cards, modals, elevated surfaces
  subtle: 'bg-oat-beige/50',  // Subtle backgrounds
  section: 'bg-cream-white',  // Section backgrounds
  elevated: 'bg-white'        // High-emphasis white cards (use sparingly)
} as const

// Text Colors
export const TEXT_COLOR = {
  primary: 'text-charcoal',
  secondary: 'text-bread-crust',
  muted: 'text-gray-600',
  light: 'text-gray-500',
  lighter: 'text-gray-400',
  heading: 'text-charcoal',
  error: 'text-red-600',
  success: 'text-avocado-green',
  warning: 'text-honey-yellow',
  white: 'text-white'
} as const

// Border Colors
export const BORDER_COLOR = {
  default: 'border-gray-200',
  light: 'border-gray-100',
  medium: 'border-gray-300',
  accent: 'border-toast-gold',
  subtle: 'border-gray-100',
  error: 'border-red-500'
} as const

// Background Colors (for non-card elements)
export const BG_COLOR = {
  error: 'bg-red-50',
  success: 'bg-avocado-green/10',
  warning: 'bg-honey-yellow/10',
  info: 'bg-toast-gold/10',
  hover: 'hover:bg-oat-beige/70',
  active: 'bg-oat-beige',
  disabled: 'bg-gray-100'
} as const

// Button Classes
export const BUTTON = {
  primary: 'bg-avocado-green text-white hover:bg-avocado-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  secondary: 'bg-toast-gold text-white hover:bg-toast-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  outline: 'border-2 border-toast-gold text-toast-gold hover:bg-toast-gold hover:text-white transition-colors',
  ghost: 'text-bread-crust hover:text-charcoal hover:bg-oat-beige transition-colors',

  // Phase 2 Extensions
  statusToggle: {
    base: 'px-3 py-1 rounded-full text-sm font-medium transition-colors',
    active: 'bg-blue-100 text-blue-800',
    inactive: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  },

  icon: 'p-1 rounded transition-colors disabled:opacity-50',
  iconRounded: 'p-2 rounded-full transition-colors disabled:opacity-50',

  counterLarge: 'w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-xl font-medium hover:bg-gray-300',
  counterSmall: 'w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-sm hover:bg-gray-200',

  gridToggle: {
    base: 'p-3 text-sm text-center border rounded-lg transition-colors capitalize',
    selected: 'border-green-500 bg-green-50 text-green-700',
    unselected: 'border-gray-300 hover:border-gray-400'
  }
} as const

// Input Classes
export const INPUT = {
  default: 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-toast-gold focus:border-transparent',
  error: 'border border-red-500 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500',

  // Phase 2 Extensions
  inline: 'w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
  numberSmall: 'w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
  withButton: 'flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'
} as const

/**
 * Checkbox System
 * Various checkbox styles and layouts
 */
export const CHECKBOX = {
  default: 'w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500',

  rounded: {
    base: 'flex-shrink-0 w-6 h-6 rounded-full border-2 transition-all',
    checked: 'bg-green-500 border-green-500 text-white',
    unchecked: 'border-gray-300 hover:border-green-400'
  },

  withLabel: 'flex items-start space-x-3'
} as const

// ============================================================================
// HELPER COMPONENTS (for consistency)
// ============================================================================

export const getLoadingSpinnerClass = (size: 'default' | 'small' | 'large' | 'tiny' = 'default'): string => {
  return LOADING_SPINNER[size]
}

export const getContinueButtonClass = (variant: 'primary' | 'secondary' = 'primary'): string => {
  return CONTINUE_BUTTON[variant]
}

// ============================================================================
// PHASE 1: COMPLEX UI PATTERNS
// ============================================================================

/**
 * Modal System
 * Consistent modal overlays, content containers, and button styles
 */
export const MODAL = {
  overlay: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4',
  content: 'bg-white rounded-lg max-w-md w-full p-6 shadow-xl',

  buttons: {
    primary: 'w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors',
    secondary: 'w-full bg-oat-beige text-bread-crust py-3 px-4 rounded-lg font-medium border-2 border-gray-300 hover:bg-oat-beige/50 transition-colors'
  }
} as const

/**
 * Dropdown System
 * Fixed position dropdowns with grid layouts (used in RecipeCard)
 */
export const DROPDOWN = {
  fixedContainer: 'fixed left-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg z-50',
  gridContent: 'grid grid-cols-2 gap-1 p-2 max-h-48 overflow-y-auto',

  item: {
    base: 'text-left px-3 py-2 text-sm sm:text-xs flex items-center rounded',
    hoverOrange: 'hover:bg-toast-gold-50',
    hoverRed: 'hover:bg-toast-gold-50',
    hoverIndigo: 'hover:bg-toast-gold-50'
  }
} as const

/**
 * Badge System
 * Various badge styles for tags, filters, and status indicators
 */
export const BADGE = {
  // Modifiable badges (used in RecipeCard for protein/cooking/cuisine)
  modifiable: {
    base: 'inline-flex items-center px-2 py-1 rounded-full text-xs border transition-colors',
    orange: {
      selected: 'bg-toast-gold-200 text-toast-gold-900 border-toast-gold-300',
      unselected: 'bg-toast-gold-100 text-toast-gold-800 border-toast-gold-200 hover:bg-toast-gold-200'
    },
    red: {
      selected: 'bg-toast-gold-200 text-toast-gold-900 border-toast-gold-300',
      unselected: 'bg-toast-gold-100 text-toast-gold-800 border-toast-gold-200 hover:bg-toast-gold-200'
    },
    indigo: {
      selected: 'bg-toast-gold-200 text-toast-gold-900 border-toast-gold-300',
      unselected: 'bg-toast-gold-100 text-toast-gold-800 border-toast-gold-200 hover:bg-toast-gold-200'
    }
  },

  // Simple filter badge
  mealTypeFilter: 'inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors',

  // Info/warning banners
  info: 'bg-avocado-green/10 border border-avocado-green/30 rounded-lg p-4',
  warning: 'bg-yellow-50 border border-yellow-200 rounded-lg p-4'
} as const

/**
 * Card System Extensions
 * Extends existing CONTAINER_BG with interactive card patterns
 */
export const CARD = {
  // Gradient interactive cards (dashboard actions)
  gradientInteractive: {
    base: 'p-6 rounded-xl cursor-pointer transition-all transform hover:scale-105 shadow-lg text-white',
    gold: 'bg-gradient-to-br from-toast-gold to-toast-gold/90 hover:from-toast-gold/90 hover:to-toast-gold/80',
    green: 'bg-gradient-to-br from-avocado-green to-avocado-green/90 hover:from-avocado-green/90 hover:to-avocado-green/80',
    coral: 'bg-gradient-to-br from-coral-pop to-coral-pop/90 hover:from-coral-pop/90 hover:to-coral-pop/80',
    brown: 'bg-gradient-to-br from-bread-crust to-bread-crust/90 hover:from-bread-crust/90 hover:to-bread-crust/80'
  },

  // Selection cards (preferences)
  selection: {
    base: 'p-4 text-left border rounded-lg transition-colors',
    selected: 'border-blue-500 bg-blue-50 text-blue-900',
    unselected: 'border-gray-300 hover:border-gray-400'
  },

  // Recipe card
  recipe: 'bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative',

  // Summary/info cards
  summary: 'bg-blue-50 border border-blue-200 rounded-lg p-4'
} as const

// Helper functions for dynamic badge classes
export const getBadgeClass = (
  type: 'orange' | 'red' | 'indigo',
  selected: boolean
): string => {
  const variant = BADGE.modifiable[type]
  return `${BADGE.modifiable.base} ${selected ? variant.selected : variant.unselected}`
}

export const getDropdownItemClass = (hoverColor: 'orange' | 'red' | 'indigo'): string => {
  const hoverKey = `hover${hoverColor.charAt(0).toUpperCase() + hoverColor.slice(1)}` as keyof typeof DROPDOWN.item
  return `${DROPDOWN.item.base} ${DROPDOWN.item[hoverKey]}`
}

export const getGradientCardClass = (color: 'gold' | 'green' | 'coral' | 'brown'): string => {
  return `${CARD.gradientInteractive.base} ${CARD.gradientInteractive[color]}`
}

export const getSelectionCardClass = (selected: boolean): string => {
  return `${CARD.selection.base} ${selected ? CARD.selection.selected : CARD.selection.unselected}`
}

// ============================================================================
// PHASE 2: MEDIUM PRIORITY UI PATTERNS
// ============================================================================

/**
 * Tab Navigation System
 * Used in MealPlanDisplay and other tabbed interfaces
 */
export const TAB = {
  button: {
    base: 'flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
    active: 'bg-toast-gold text-white',
    inactive: 'bg-oat-beige/60 text-charcoal/70 hover:bg-oat-beige/70'
  },

  badge: {
    base: 'px-1.5 py-0.5 rounded text-xs',
    active: 'bg-toast-gold/80',
    inactive: 'bg-white text-charcoal/50'
  },

  container: 'flex space-x-1 mb-3 overflow-x-auto pb-2'
} as const

/**
 * List Item System
 * Used in shopping lists and other item lists
 */
export const LIST_ITEM = {
  shopping: {
    base: 'group bg-white rounded-lg p-3 shadow-sm border-l-4 transition-all',
    checked: 'border-green-400 bg-green-50',
    unchecked: 'border-gray-200 hover:shadow-md'
  }
} as const

/**
 * Progress Bar System
 * Progress indicators and completion bars
 */
export const PROGRESS = {
  container: 'w-full bg-gray-200 rounded-full h-2',
  fill: 'bg-green-500 h-2 rounded-full transition-all duration-300',

  setup: {
    container: 'w-full bg-gray-200 rounded-full h-2',
    fill: 'bg-blue-600 h-2 rounded-full transition-all duration-300'
  }
} as const

/**
 * Banner/Alert System
 * Warning and alert banners
 */
export const BANNER = {
  warning: 'p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800',
  alert: 'p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800'
} as const

/**
 * Section/Layout System
 * Common layout patterns
 */
export const LAYOUT = {
  dashboardGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  loadingContainer: 'flex items-center justify-center min-h-[400px]',

  section: {
    header: 'w-full flex items-center justify-between p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow',
    expandable: 'space-y-4 p-4 bg-gray-50 rounded-lg'
  }
} as const

/**
 * Tag System
 * Removable tags and chips
 */
export const TAG = {
  removable: 'inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-sm',

  chip: {
    base: 'px-3 py-2 text-sm rounded-full border transition-colors',
    selected: 'border-orange-500 bg-orange-50 text-orange-700',
    unselected: 'border-gray-300 hover:border-gray-400'
  }
} as const

// Phase 2 Helper Functions

export const getStatusToggleClass = (isActive: boolean): string => {
  return `${BUTTON.statusToggle.base} ${isActive ? BUTTON.statusToggle.active : BUTTON.statusToggle.inactive}`
}

export const getGridToggleClass = (isSelected: boolean): string => {
  return `${BUTTON.gridToggle.base} ${isSelected ? BUTTON.gridToggle.selected : BUTTON.gridToggle.unselected}`
}

export const getTabButtonClass = (isActive: boolean): string => {
  return `${TAB.button.base} ${isActive ? TAB.button.active : TAB.button.inactive}`
}

export const getTabBadgeClass = (isActive: boolean): string => {
  return `${TAB.badge.base} ${isActive ? TAB.badge.active : TAB.badge.inactive}`
}

export const getShoppingListItemClass = (isChecked: boolean): string => {
  return `${LIST_ITEM.shopping.base} ${isChecked ? LIST_ITEM.shopping.checked : LIST_ITEM.shopping.unchecked}`
}

export const getCheckboxRoundedClass = (isChecked: boolean): string => {
  return `${CHECKBOX.rounded.base} ${isChecked ? CHECKBOX.rounded.checked : CHECKBOX.rounded.unchecked}`
}

export const getChipClass = (isSelected: boolean): string => {
  return `${TAG.chip.base} ${isSelected ? TAG.chip.selected : TAG.chip.unselected}`
}

// ============================================================================
// PHASE 3: LOW PRIORITY FOUNDATIONAL PATTERNS
// ============================================================================

/**
 * Typography Scale
 * Consistent text sizing and hierarchy across the application
 */
export const TYPOGRAPHY = {
  // Headings
  h1: 'text-3xl font-bold text-charcoal',
  h2: 'text-2xl font-bold text-charcoal',
  h3: 'text-xl font-semibold text-charcoal',
  h4: 'text-lg font-semibold text-charcoal',

  // Body text
  body: 'text-base text-charcoal',
  bodySmall: 'text-sm text-charcoal',
  bodyLarge: 'text-lg text-charcoal',

  // UI text
  label: 'text-sm font-medium text-bread-crust',
  caption: 'text-xs text-gray-600',
  captionSmall: 'text-xs text-gray-500',

  // Interactive text
  link: 'text-sm text-blue-600 hover:text-blue-800 hover:underline',
  buttonText: 'text-sm font-medium',

  // Specialized
  code: 'font-mono text-sm bg-gray-100 px-1 rounded',
  error: 'text-sm text-red-600',
  success: 'text-sm text-avocado-green'
} as const

/**
 * Spacing Presets
 * Common padding, margin, and gap combinations
 */
export const SPACING = {
  // Padding presets
  inputSm: 'px-2 py-1',
  input: 'px-3 py-2',
  inputLg: 'px-4 py-3',
  button: 'px-4 py-2',
  buttonSm: 'px-3 py-1.5',
  buttonLg: 'px-6 py-3',
  card: 'p-4',
  cardLg: 'p-6',
  cardSm: 'p-3',
  section: 'p-6',

  // Gap presets
  stackTight: 'space-y-2',
  stack: 'space-y-4',
  stackLoose: 'space-y-6',
  inlineTight: 'space-x-1',
  inline: 'space-x-2',
  inlineLoose: 'space-x-4',
  grid: 'gap-4',
  gridTight: 'gap-2',
  gridLoose: 'gap-6'
} as const

/**
 * Size Constants
 * Icon sizes, button heights, borders, shadows
 */
export const SIZE = {
  // Icon sizes
  iconXs: 'w-3 h-3',
  iconSm: 'w-4 h-4',
  icon: 'w-5 h-5',
  iconLg: 'w-6 h-6',
  iconXl: 'w-8 h-8',

  // Button heights
  buttonSm: 'h-8',
  button: 'h-10',
  buttonLg: 'h-12',

  // Rounded corners
  roundedNone: 'rounded-none',
  roundedSm: 'rounded',
  rounded: 'rounded-lg',
  roundedLg: 'rounded-xl',
  roundedFull: 'rounded-full',

  // Shadows
  shadowNone: 'shadow-none',
  shadowSm: 'shadow-sm',
  shadow: 'shadow-md',
  shadowLg: 'shadow-lg',
  shadowXl: 'shadow-xl',

  // Max widths
  maxWXs: 'max-w-xs',
  maxWSm: 'max-w-sm',
  maxWMd: 'max-w-md',
  maxWLg: 'max-w-lg',
  maxWXl: 'max-w-xl',
  maxW2xl: 'max-w-2xl'
} as const
